import { writeBatch, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { assignTerritory } from '../../utils/pointInPolygon';

// Lead fields we can import + their default CSV header names (auto-mapping).
export const IMPORT_FIELDS = {
  contactId: 'Contact ID',
  businessName: 'Business Name',
  category: 'Channel',
  tier: 'Tier',
  stage: 'Stage',
  leadScore: 'Lead Score',
  leadValue: 'Lead Value',
  phone: 'Phone',
  email: 'Email',
  contactPerson: 'Contact Person',
  area: 'Area',
  lat: 'Lat',
  lng: 'Lng',
};
const NUMERIC = new Set(['leadScore', 'leadValue', 'lat', 'lng']);

// Auto-map field→column where a CSV header matches (case-insensitive).
export function autoMap(headers) {
  const lower = new Map(headers.map((h) => [h.toLowerCase(), h]));
  const mapping = {};
  for (const [field, label] of Object.entries(IMPORT_FIELDS)) {
    const hit = lower.get(label.toLowerCase()) || lower.get(field.toLowerCase());
    if (hit) mapping[field] = hit;
  }
  return mapping;
}

// Map raw CSV objects → lead shapes, stamped with tenant/store. Optionally
// auto-assigns territory by lat/lng when territories are supplied. A default
// Agent (ownerUid/ownerName) and default Territory can be applied to every
// imported row (CRM revisions Task 1).
export function mapRowsToLeads(objs, mapping, {
  tenantId, storeId, territories = [], ownerUid = null, ownerName = null, territoryId = null,
}) {
  return objs.map((o) => {
    const lead = {};
    for (const [field, col] of Object.entries(mapping)) {
      if (!col) continue;
      let v = o[col];
      if (v === undefined || v === '') continue;
      if (NUMERIC.has(field)) v = Number(v);
      lead[field] = v;
    }
    lead.tenantId = tenantId;
    lead.storeId = storeId;
    if (lead.territoryId == null && typeof lead.lat === 'number' && typeof lead.lng === 'number') {
      lead.territoryId = assignTerritory([lead.lat, lead.lng], territories);
    }
    // Fall back to the import's chosen default territory when none derived.
    if (lead.territoryId == null && territoryId) lead.territoryId = territoryId;
    // Attribute every imported lead to the chosen Agent (when one is picked).
    if (ownerUid) { lead.ownerUid = ownerUid; lead.ownerName = ownerName; }
    return lead;
  });
}

// Split into fresh vs duplicate by Contact ID (dupes = already in `existingIds`
// or repeated within the file, or missing an id).
export function dedupeByContactId(leads, existingIds = new Set()) {
  const seen = new Set(existingIds);
  const fresh = [];
  const dupes = [];
  for (const l of leads) {
    const id = l.contactId;
    if (!id) { dupes.push({ ...l, _reason: 'missing Contact ID' }); continue; }
    if (seen.has(id)) { dupes.push({ ...l, _reason: 'duplicate Contact ID' }); continue; }
    seen.add(id);
    fresh.push(l);
  }
  return { fresh, dupes };
}

// Batch-write fresh leads. doc id = Contact ID (the dedupe key); tenant stamped.
export async function writeLeads(fresh, actor) {
  let written = 0;
  for (let i = 0; i < fresh.length; i += 450) {
    const chunk = fresh.slice(i, i + 450);
    const batch = writeBatch(db);
    const ts = Date.now();
    for (const l of chunk) {
      const { contactId, ...rest } = l;
      batch.set(doc(db, 'leads', contactId), {
        ...rest,
        lifecycle: rest.lifecycle ?? 'prospect',
        createdAt: ts,
        updatedAt: ts,
        updatedBy: actor?.uid ?? null,
        updatedByName: actor?.name ?? null,
      });
    }
    await batch.commit();
    written += chunk.length;
  }
  return written;
}
